import Select from 'react-select';
import BaseInputBlock, { BaseInputBlockProps } from './base-input-block';
import { useFillableBlockResponse } from './common';
import { SelectionBlock as ISelectionBlock, SelectOptionType, SelectionType } from '@/lib/types';

export default function SelectionBlock({ block }: BaseInputBlockProps<ISelectionBlock>) {
  const { payload, updatePayload } = useFillableBlockResponse({ block });

  function getSelectedItems(selects?: Array<string>) {
    if (typeof selects === 'string') {
      return block.properties.options.find((opt) => selects === opt.value);
    }
    return selects && selects?.length
      ? block.properties.options.filter((opt) => selects.find((sel) => sel === opt.value))
      : [];
  }

  function setItems(opts?: SelectOptionType[]) {
    updatePayload({
      value:
        opts?.map((item) => {
          return item.value;
        }) ?? [],
    });
  }

  function setValue(opt?: SelectOptionType) {
    updatePayload({
      value: opt ? [opt?.value] : null,
    });
  }

  return (
    <BaseInputBlock block={block}>
      <Select
        value={getSelectedItems(payload?.value)}
        onChange={(e) =>
          block.properties.selectionType === SelectionType.Multi
            ? setItems(e as SelectOptionType[])
            : setValue(e as SelectOptionType)
        }
        isMulti={block.properties.selectionType === SelectionType.Multi}
        options={block.properties.options}
        menuPosition={'fixed'}
        isClearable={true}
        closeMenuOnSelect={block.properties.selectionType === SelectionType.Single}
        // react-select is third-party and outside Chakra's recipe system, so
        // it's styled by hand against the DS runtime CSS variables
        // (design-system/ds-runtime.css) to match the filled DS field +
        // bordered menu surface. No hardcoded values — all tokens.
        styles={{
          container: (provided) => ({
            ...provided,
            width: '100%',
          }),
          control: (base, state) => ({
            ...base,
            minHeight: 40,
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-body)',
            backgroundColor: 'var(--bg-surface)',
            borderColor: state.isFocused ? 'var(--brand-primary)' : 'var(--border-strong)',
            borderRadius: 'var(--r-md)',
            boxShadow: state.isFocused ? '0 0 0 3px var(--ring)' : 'none',
            transition: '.15s',
            '&:hover': {
              borderColor: state.isFocused ? 'var(--brand-primary)' : 'var(--border-strong)',
            },
          }),
          valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
          input: (base) => ({ ...base, fontSize: 14, color: 'var(--text-body)' }),
          singleValue: (base) => ({ ...base, fontSize: 14, color: 'var(--text-body)' }),
          placeholder: (base) => ({ ...base, fontSize: 14, color: 'var(--text-faint)' }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: 'var(--badge-gray-bg)',
            borderRadius: 'var(--r-sm)',
          }),
          multiValueLabel: (base) => ({ ...base, fontSize: 14, color: 'var(--badge-gray-fg)' }),
          multiValueRemove: (base) => ({
            ...base,
            color: 'var(--badge-gray-fg)',
            borderRadius: 'var(--r-sm)',
            '&:hover': { backgroundColor: 'var(--border-strong)', color: 'var(--text-strong)' },
          }),
          indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--border)' }),
          dropdownIndicator: (base) => ({ ...base, color: 'var(--text-muted)' }),
          clearIndicator: (base) => ({ ...base, color: 'var(--text-muted)' }),
          menu: (base) => ({
            ...base,
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-md)',
            overflow: 'hidden',
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 100,
          }),
          option: (base, state) => ({
            ...base,
            fontSize: 14,
            color: 'var(--text-body)',
            backgroundColor:
              state.isSelected || state.isFocused ? 'var(--bg-subtle)' : 'transparent',
            '&:active': { backgroundColor: 'var(--bg-subtle)' },
          }),
        }}
      />
    </BaseInputBlock>
  );
}
